import { useCallback } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { AddAndRemoveJsxChange } from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useExtractElementStore } from "@/components/Canvas/stores/extractElementStore";
import {
  useSelectedNavTreeNode,
  useMultiselectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useActiveCanvas, useActiveProject, useCanvasId } from "@/utils/utils";
import { getComponentFileFromNavTreeNodes } from "@/utils/jsx/extractElementsUtils";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { setTempoClipboardData } from "@/components/Canvas/utils/canvasClipboardData";
import { compressNavTree } from "@/utils/navTreeCompression";
import { convertHtmlToJsx } from "@/components/Canvas/IframePool/utils";
import { createDummyComponent } from "@/components/AppHeader/utils";
import { ReactFramework } from "@/utils/templateProjects";

export const useDetachInstance = () => {
  const canvasId = useCanvasId();
  const activeCanvas = useActiveCanvas();
  const project = useActiveProject();

  const { selectedElement } = useSelectedNavTreeNode();
  const { multiselectedElements, multiselectedStoryboardIds } =
    useMultiselectedNavTreeNode();
  const { selectedNavTreeNode } = useSelectedNavTreeNode();
  const { multiselectedNavTreeNodes } = useMultiselectedNavTreeNode();

  const executeCommand = useDesignCommandManager(
    (store) => store.executeCommand,
  );

  const detachInstance = useCallback(async () => {
    if (multiselectedStoryboardIds?.length || multiselectedElements?.length) {
      toast.error("Can only detach instance for a single element");
      return;
    }

    const selectedElementHtml =
      useExtractElementStore.getState().selectedElementHTML;
    if (!selectedElementHtml) {
      toast.error("No base HTML found for selected element");
      return;
    }

    const detachJsx = convertHtmlToJsx(selectedElementHtml);

    // Wrap with blank storyboard
    const detachElementFileContents = createDummyComponent(
      detachJsx,
      project?.framework as ReactFramework,
      "DetachedElement",
    );

    // File path doesn't matter because it is all static html
    const detachElementDummyFilePath = `${canvasId}/dummy.tsx`;

    if (!detachElementFileContents) {
      toast.error("Could not detach element");
      return;
    }

    // We specifically make a new nav tree from the processed html because the
    // nav tree for this element contains components that we do not want
    const {
      html: processedHtml,
      navTree: processedNavTree,
      error,
    } = processInstantUpdateData({
      storyboardId: selectedElement.storyboardId,
      canvasId: canvasId,
      projectSubfolderPath: project?.subfolderPath,
      instantUpdateData: {
        html: selectedElementHtml || "",
      },
      wrapInShell: false,
    });

    if (error) {
      toast.error("Could not detach element: " + error);
      return;
    }

    const storyboardForElement = activeCanvas?.storyboards?.find(
      (storyboard) => storyboard.id === selectedElement.storyboardId,
    );

    const selectedElementBoundingBox =
      useExtractElementStore.getState().selectedElementBoundingBox;

    setTempoClipboardData(undefined, {
      selectedElementKey: selectedElement.getKey(),
      multiselectedElementKeys: multiselectedElements?.map((element) =>
        element.getKey(),
      ),
      fileContents: detachElementFileContents,
      fileContentsSourceFilename: detachElementDummyFilePath ?? undefined,
      htmlForInstantUpdate: processedHtml ?? "",
      compressedNavTreeNodesForInstantUpdate: (processedNavTree
        ? [processedNavTree]
        : []
      )
        .map((node) => compressNavTree(node))
        .filter((result) => result !== null),
      width: selectedElementBoundingBox?.width,
      height: selectedElementBoundingBox?.height,
    });

    toast.success("Copied detached element to clipboard");
    return;
  }, [
    canvasId,
    activeCanvas,
    project,
    selectedElement,
    multiselectedElements,
    multiselectedStoryboardIds,
  ]);

  return detachInstance;
};
